import {
  JobsApi,
  BackupsApi,
  ListBackupJobsTypesApi,
  ListRestoreJobsTypesApi,
  ListScanJobsTypesApi,
  ListBackupJobErrorCodesApi,
  ListRestoreJobErrorCodesApi,
  ListScanJobErrorCodesApi,
} from '@repo/api-gw-sdk';
import type { Condition, Configuration } from '@repo/api-gw-sdk';
import type { ColumnSort } from '@tanstack/react-table';

import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';

import type { HttpClient } from './httpClient';

const baseUrl = '/jobs';

export const jobs = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const jobsApi = new JobsApi(config);
  const backupsApi = new BackupsApi(config);
  const listBackupJobTypesApi = new ListBackupJobsTypesApi(config);
  const listScanJobTypesApi = new ListScanJobsTypesApi(config);
  const listRestoreJobTypesApi = new ListRestoreJobsTypesApi(config);
  const listBackupJobErrorCodesApi = new ListBackupJobErrorCodesApi(config);
  const listRestoreJobErrorCodesApi = new ListRestoreJobErrorCodesApi(config);
  const listScanJobErrorCodesApi = new ListScanJobErrorCodesApi(config);
  return {
    scan: {
      useJob: (jobId: string) =>
        httpClient.execute(
          jobsApi.getScanJob.bind(jobsApi),
          baseUrl,
          '/scan',
          currentProjectId,
          jobId
        ),
      useList: (
        pageIndex: number,
        pageSize: number,
        sorting: ColumnSort | undefined,
        columnFilters: Condition | undefined
      ) =>
        httpClient.execute(
          jobsApi.listScanJobsInternal.bind(jobsApi),
          baseUrl,
          '/scan',
          currentProjectId,
          pageIndex,
          pageSize,
          sorting
            ? `${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`
            : undefined,
          columnFilters ? toJsonText(columnFilters) : undefined
        ),
      useListTypes: () =>
        httpClient.execute(
          listScanJobTypesApi.listScanJobsTypes.bind(listScanJobTypesApi),
          baseUrl,
          '/list-scan-job-types',
          currentProjectId
        ),
      useListErrorCodes: () =>
        httpClient.execute(
          listScanJobErrorCodesApi.listScanJobErrorCodes.bind(
            listScanJobErrorCodesApi
          ),
          baseUrl,
          '/list-scan-job-error-codes',
          currentProjectId
        ),
    },
    backup: {
      create: (
        resourceId: string,
        vaultId: string,
        retentionDays: number,
        isPitrOnDemand = false
      ) => {
        return backupsApi.takeSnapshot(currentProjectId, resourceId, {
          retentionDays,
          vaultId,
          isPitrOnDemand,
        });
      },
      createNative: (
        resourceId: string,
        retentionDays: number,
        targetRegion?: string
      ) => {
        return backupsApi.takeNativeSnapshot(currentProjectId, resourceId, {
          retentionDays,
          targetRegion,
        });
      },
      useJob: (jobId: string) =>
        httpClient.execute(
          jobsApi.getBackupJob.bind(jobsApi),
          baseUrl,
          '/backup',
          currentProjectId,
          jobId
        ),
      useList: (
        pageIndex: number,
        pageSize: number,
        sorting: ColumnSort | undefined,
        columnFilters: Condition | undefined
      ) =>
        httpClient.execute(
          jobsApi.listBackupJobsInternal.bind(jobsApi),
          baseUrl,
          '/backup',
          currentProjectId,
          pageIndex,
          pageSize,
          sorting
            ? `${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`
            : undefined,
          columnFilters ? toJsonText(columnFilters) : undefined
        ),
      useListTypes: () =>
        httpClient.execute(
          listBackupJobTypesApi.listBackupJobsTypes.bind(listBackupJobTypesApi),
          baseUrl,
          '/list-backup-job-types',
          currentProjectId
        ),
      useListErrorCodes: () =>
        httpClient.execute(
          listBackupJobErrorCodesApi.listBackupJobErrorCodes.bind(
            listBackupJobErrorCodesApi
          ),
          baseUrl,
          '/list-backup-job-error-codes',
          currentProjectId
        ),
    },
    restore: {
      cancel: (jobId: string) => {
        return jobsApi.cancelRestoreJob(currentProjectId, jobId);
      },
      useJob: (jobId: string) =>
        httpClient.execute(
          jobsApi.getRestoreJob.bind(jobsApi),
          baseUrl,
          '/restore',
          currentProjectId,
          jobId
        ),
      useList: (
        pageIndex: number,
        pageSize: number,
        sorting: ColumnSort | undefined,
        columnFilters: Condition | undefined
      ) =>
        httpClient.execute(
          jobsApi.listRestoreJobsInternal.bind(jobsApi),
          baseUrl,
          '/restore',
          currentProjectId,
          pageIndex,
          pageSize,
          sorting
            ? `${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`
            : undefined,
          columnFilters ? toJsonText(columnFilters) : undefined
        ),
      useListTypes: () =>
        httpClient.execute(
          listRestoreJobTypesApi.listRestoreJobsTypes.bind(
            listRestoreJobTypesApi
          ),
          baseUrl,
          '/list-restore-job-types',
          currentProjectId
        ),
      useListErrorCodes: () =>
        httpClient.execute(
          listRestoreJobErrorCodesApi.listRestoreJobErrorCodes.bind(
            listRestoreJobErrorCodesApi
          ),
          baseUrl,
          '/list-restore-job-error-codes',
          currentProjectId
        ),
    },
  };
};
